import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'

import { updateDirectoryLocation } from '~/api/user-file'
import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useCreateDirectoryMutation, keys as userFileQueryKeys } from '~/queries/user-file'
import { displayToast } from '~/utils/toast'

interface ModalFormProps {
  directoryId: string
  directoryName?: string
  initialDirectory?: { fileId: string; fileName: string }
  onCloseModal: VoidFunction
  onSuccess: VoidFunction
  path?: string
}

interface IValues {
  name: string
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const ManageDirectoryForm = ({
  directoryId,
  directoryName,
  initialDirectory,
  onCloseModal,
  onSuccess,
  path,
}: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = new QueryClient()

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<IValues>({
    defaultValues: { name: initialDirectory?.fileName || '' },
    resolver: yupResolver(validationSchema),
  })

  /** Mutation */

  const { mutateAsync: createDirectoryMutation } = useCreateDirectoryMutation()

  /** Handlers */

  const handleCloseModal = () => {
    reset()
    onCloseModal()
  }

  const handleFormSubmit = async (values: IValues) => {
    const { name } = values

    try {
      const newPath = directoryName === 'root' ? '/' : path === '/' ? `/${directoryName}` : `${path}/${directoryName}`
      if (initialDirectory) {
        await updateDirectoryLocation({ directoryId: initialDirectory.fileId, name, path: newPath })
      } else {
        await createDirectoryMutation({ name, path: newPath })
      }
      await queryClient.invalidateQueries({ queryKey: userFileQueryKeys.listByDirectory(directoryId) })
      onSuccess()
      reset()
    } catch (e) {
      if (isAxiosError(e)) {
        const error = e as AxiosError<IApiError>
        switch (error.response?.data.kind) {
          case 'invalid_name':
            displayToast(`Invalid directory name. ${error.response.data.message}`, 'error')
            break
          default:
            captureException(e)
            displayToast(`Cannot ${initialDirectory ? 'update' : 'create'} this directory`, 'error')
        }
      } else {
        captureException(e)
        displayToast(`Cannot ${initialDirectory ? 'update' : 'create'} this directory`, 'error')
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <TextInput {...register('name')} autoFocus error={errors.name?.message} label="Name" required />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button type="submit">{initialDirectory ? 'Update' : 'Create'}</Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends ModalFormProps {
  isOpen: boolean
}

const ManageDirectoryModal = ({ initialDirectory, isOpen, onCloseModal, ...props }: ModalProps): ReactElement => {
  return (
    <Modal isOpen={isOpen} onCloseModal={onCloseModal} shouldCloseOnOverlayClick={false}>
      <ModalHeader>{initialDirectory ? 'Update' : 'Create a new'} directory</ModalHeader>
      <ManageDirectoryForm {...props} initialDirectory={initialDirectory} onCloseModal={onCloseModal} />
    </Modal>
  )
}

export default ManageDirectoryModal
